import { __awaiter, __generator, __read, __spreadArray } from "tslib";
import StructuredStorage, { TransactionMode } from '@tencent/memoire-structured-storage';
import IDBEngine from '@tencent/memoire-idb-engine';
import { BlobDataModel, BlobMetadataModel } from './models';
import BlobConverter, { BlobEncoding } from './utils/blob-converter';
import PathUtils from './utils/path-utils';
var storage = new StructuredStorage({
    name: 'BlobStorage',
    version: 1,
    engine: new IDBEngine(),
});
storage.register(BlobDataModel);
storage.register(BlobMetadataModel);
/**
 * The BlobStorage provides APIs to persist Blob data in browser.
 */
var BlobStorage = /** @class */ (function () {
    function BlobStorage(namespace) {
        if (namespace === void 0) { namespace = []; }
        this.namespace = namespace;
        this.prefix = PathUtils.join.apply(PathUtils, __spreadArray([PathUtils.separator], __read(namespace)));
    }
    /**
     * Write a blob to storage.
     * @param path a path to this blob
     * @param blob a Blob instance
     * @param metadata the metadata to attach to this blob
     * @returns the absolute path of saved blob
     */
    BlobStorage.prototype.write = function (path, blob, _a) {
        var _b = _a === void 0 ? {} : _a, additions = _b.additions;
        return __awaiter(this, void 0, void 0, function () {
            var absPath, data, metadata, finalPath;
            var _this = this;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        absPath = this.getAbsolutePath(path);
                        return [4 /*yield*/, BlobDataModel.fromBlob(absPath, blob)];
                    case 1:
                        data = _c.sent();
                        return [4 /*yield*/, BlobMetadataModel.fromBlob(absPath, blob, additions !== null && additions !== void 0 ? additions : {})];
                    case 2:
                        metadata = _c.sent();
                        return [4 /*yield*/, storage.transaction([BlobDataModel, BlobMetadataModel], TransactionMode.READ_WRITE, function (tx) { return __awaiter(_this, void 0, void 0, function () {
                                return __generator(this, function (_a) {
                                    switch (_a.label) {
                                        case 0: return [4 /*yield*/, tx.collection(BlobDataModel).upsert(data)];
                                        case 1:
                                            _a.sent();
                                            return [2 /*return*/, tx.collection(BlobMetadataModel).upsert(metadata)];
                                    }
                                });
                            }); })];
                    case 3:
                        finalPath = _c.sent();
                        return [2 /*return*/, finalPath];
                }
            });
        });
    };
    BlobStorage.prototype.read = function (path, encoding) {
        if (encoding === void 0) { encoding = BlobEncoding.Blob; }
        return __awaiter(this, void 0, void 0, function () {
            var blob;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (encoding === BlobEncoding.ArrayBuffer) {
                            return [2 /*return*/, this.readAsArrayBuffer(path)];
                        }
                        return [4 /*yield*/, this.readAsBlob(path)];
                    case 1:
                        blob = _a.sent();
                        if (!blob)
                            return [2 /*return*/, null];
                        return [2 /*return*/, BlobConverter.convertBlob(blob, encoding)];
                }
            });
        });
    };
    /**
     * Remove a blob by it's path.
     * @param path a path to saved blob
     * @returns the absolute path of deleted blob
     */
    BlobStorage.prototype.remove = function (path) {
        return __awaiter(this, void 0, void 0, function () {
            var finalPath;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, storage.transaction([BlobDataModel, BlobMetadataModel], TransactionMode.READ_WRITE, function (tx) { return __awaiter(_this, void 0, void 0, function () {
                            var absPath;
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0:
                                        absPath = this.getAbsolutePath(path);
                                        return [4 /*yield*/, tx.collection(BlobMetadataModel).delete(absPath)];
                                    case 1:
                                        _a.sent();
                                        return [2 /*return*/, tx.collection(BlobDataModel).delete(absPath)];
                                }
                            });
                        }); })];
                    case 1:
                        finalPath = _a.sent();
                        return [2 /*return*/, finalPath];
                }
            });
        });
    };
    /**
     * Get the metadata of a blob.
     * @param path a path to saved blob
     * @returns the metadata of the blob
     */
    BlobStorage.prototype.stat = function (path) {
        return __awaiter(this, void 0, void 0, function () {
            var metadata;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, storage.collection(BlobMetadataModel).get(this.getAbsolutePath(path))];
                    case 1:
                        metadata = _a.sent();
                        if (!metadata)
                            return [2 /*return*/, null];
                        return [2 /*return*/, metadata];
                }
            });
        });
    };
    /**
     * Attach additional metadata to blob.
     * @param path a path to saved blob
     * @param additions the additional data to attach
     * @returns the absolute path of attached blob
     */
    BlobStorage.prototype.attach = function (path, additions) {
        return __awaiter(this, void 0, void 0, function () {
            var _a, finalPath;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, storage
                            .collection(BlobMetadataModel)
                            .where({ path: this.getAbsolutePath(path) })
                            .update(function (metadata) { return metadata.setAdditions(additions); })];
                    case 1:
                        _a = __read.apply(void 0, [_b.sent(), 1]), finalPath = _a[0];
                        return [2 /*return*/, finalPath];
                }
            });
        });
    };
    /**
     * Read a blob by it's path as ArrayBuffer encoding.
     * @param path a path to saved blob
     *
     * @returns an ArrayBuffer
     */
    BlobStorage.prototype.readAsArrayBuffer = function (path) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0: return [4 /*yield*/, this.readAsDataModel(path)];
                    case 1: return [2 /*return*/, (_b = (_a = (_c.sent())) === null || _a === void 0 ? void 0 : _a.data) !== null && _b !== void 0 ? _b : null];
                }
            });
        });
    };
    /**
     * Read a blob by it's path as Blob encoding.
     * @param path a path to saved blob
     *
     * @returns a Blob
     */
    BlobStorage.prototype.readAsBlob = function (path) {
        return __awaiter(this, void 0, void 0, function () {
            var model;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.readAsDataModel(path)];
                    case 1:
                        model = _a.sent();
                        if (!model)
                            return [2 /*return*/, null];
                        return [2 /*return*/, new Blob([model.data], { type: model.type })];
                }
            });
        });
    };
    /**
     * Read a blob by it's path as DataURL encoding.
     * @param path a path to saved blob
     *
     * @returns a DataURL
     */
    BlobStorage.prototype.readAsDataURL = function (path) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.read(path, BlobEncoding.DataURL)];
            });
        });
    };
    /**
     * Read a blob by it's path as Base64 encoding.
     * @param path a path to saved blob
     *
     * @returns a Base64
     */
    BlobStorage.prototype.readAsBase64 = function (path) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.read(path, BlobEncoding.Base64)];
            });
        });
    };
    /**
     * Read a blob by it's path as ObjectURL encoding.
     * @param path a path to saved blob
     *
     * @returns a ObjectURL
     */
    BlobStorage.prototype.readAsObjectURL = function (path) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.read(path, BlobEncoding.ObjectURL)];
            });
        });
    };
    /**
     * List absolute paths of all blobs in the namespace
     * @param namespace a namespace
     * @returns absolute paths of all blobs in the namespace
     */
    BlobStorage.prototype.list = function (namespace) {
        if (namespace === void 0) { namespace = []; }
        return __awaiter(this, void 0, void 0, function () {
            var metadata, paths;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        metadata = storage.collection(BlobMetadataModel);
                        return [4 /*yield*/, metadata.where(this.belongsToNamespace(namespace)).keys()];
                    case 1:
                        paths = _a.sent();
                        return [2 /*return*/, paths];
                }
            });
        });
    };
    /**
     * Clear all blob in the namespace
     * @param namespace a namespace
     * @returns absolute paths of deleted blobs
     */
    BlobStorage.prototype.clear = function (namespace) {
        if (namespace === void 0) { namespace = []; }
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                return [2 /*return*/, storage.transaction([BlobDataModel, BlobMetadataModel], TransactionMode.READ_WRITE, function (tx) { return __awaiter(_this, void 0, void 0, function () {
                        var query, metadata, data, paths;
                        return __generator(this, function (_a) {
                            switch (_a.label) {
                                case 0:
                                    query = this.belongsToNamespace(namespace);
                                    metadata = tx.collection(BlobMetadataModel).where(query);
                                    data = tx.collection(BlobDataModel).where(query);
                                    return [4 /*yield*/, data.clear()];
                                case 1:
                                    _a.sent();
                                    return [4 /*yield*/, metadata.clear()];
                                case 2:
                                    paths = _a.sent();
                                    return [2 /*return*/, paths];
                            }
                        });
                    }); })];
            });
        });
    };
    BlobStorage.prototype.readAsDataModel = function (path) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, storage.collection(BlobDataModel).get(this.getAbsolutePath(path))];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    BlobStorage.prototype.belongsToNamespace = function (namespace) {
        if (namespace === void 0) { namespace = []; }
        var search = PathUtils.join.apply(PathUtils, __spreadArray(__spreadArray([], __read(this.namespace)), __read(namespace))).split(PathUtils.separator)
            .filter(Boolean);
        return {
            namespace: {
                '>=': search,
                '<': __spreadArray(__spreadArray([], __read(search)), [[]]),
            },
        };
    };
    BlobStorage.prototype.getAbsolutePath = function (path) {
        if (PathUtils.isAbsolute(path)) {
            return PathUtils.normalize(path);
        }
        return PathUtils.join(this.prefix, path);
    };
    return BlobStorage;
}());
export default BlobStorage;
//# sourceMappingURL=blob-storage.js.map